import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

import { TutorBillingDatePreset } from './enums'
import { useTutorBillingStore } from './store'
import { TUTOR_BILLING_DATE_PRESET_OPTIONS } from '../config/tutor-billing'

interface FilterDatePresetReturn {
  preset: WritableComputedRef<TutorBillingDatePreset | undefined>
  customRange: WritableComputedRef<Date[] | null>
  isCustom: ComputedRef<boolean>
  presetOptions: AppSelectOption<TutorBillingDatePreset>[]
}

export const useFilterDatePreset = (): FilterDatePresetReturn => {
  const store = useTutorBillingStore()

  const preset = computed<TutorBillingDatePreset | undefined>({
    get: () => store.getFilters.datePreset ?? undefined,
    set: (value) => store.setFilterDate({ datePreset: value ?? null, dateFrom: null, dateTo: null }),
  })

  const isCustom = computed<boolean>(() => store.getFilters.datePreset === TutorBillingDatePreset.Custom)

  const customRange = computed<Date[] | null>({
    get: () => {
      const { dateFrom, dateTo } = store.getFilters
      if (!dateFrom || !dateTo) return null
      return [dayjs(dateFrom).toDate(), dayjs(dateTo).toDate()]
    },
    set: (value) => {
      const [from, to] = value ?? []

      if (!from && !to) {
        store.setFilterDate({ datePreset: TutorBillingDatePreset.Custom, dateFrom: null, dateTo: null })
        return
      }

      if (!from || !to) return

      store.setFilterDate({
        datePreset: TutorBillingDatePreset.Custom,
        dateFrom: dayjs(from).format(DateFormat.DefaultDate),
        dateTo: dayjs(to).format(DateFormat.DefaultDate),
      })
    },
  })

  return {
    preset,
    customRange,
    isCustom,
    presetOptions: TUTOR_BILLING_DATE_PRESET_OPTIONS,
  }
}
